// src/pages/AdminPropertyPage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
    PageContainer,
    HeaderBar,
    Title,
    HeaderActions,
    SaveButton,
    BackButton,
    Tabs,
    TabButton,
    ScrollArea,
    Card,
    ErrorPopup,
    CloseButton,
    OwnerCard,
    AvatarBox,
    OwnerInfoWrapper,
    OwnerNameBlock,
    OwnerInfoGrid,
    OwnerRow,
    Label,
    Value,
} from "../style-comps/AdminPropertyPage.styles";
import OverviewTab from "../pages/OverviewTab";
// import ContractTab from "../pages/ContractTab";
// import InvoiceTab from "../pages/InvoiceTab";
// import InspectionTab from "../pages/InspectionTab";
import { getListingChildrenById, getListingPlatformUrls, getUserListingsByListingId, updateListingsBatch, getListingDetail } from "../services/api";

// Statement / Invoice / Inspection / Contract 元件都還沒接上（見上方註解掉的 import），
// 在這裡才算「已實作」，其餘 Tab 一律鎖定。
const IMPLEMENTED_TABS = ["Overview"];

const AdminPropertyPage = () => {
    const { listingId } = useParams();
    const token = localStorage.getItem("authToken");

    const [activeTab, setActiveTab] = useState("Overview");
    const [listing, setListing] = useState(null);
    const [parentRow, setParentRow] = useState(null);
    const [childrenRows, setChildrenRows] = useState([]);
    const [originalParentRow, setOriginalParentRow] = useState(null);
    const [originalChildrenRows, setOriginalChildrenRows] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const [sp] = useSearchParams();
    const fromQuery = location.state?.fromQuery || sp.get("from") || "";
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [owners, setOwners] = useState([]);
    const [hostPartners, setHostPartners] = useState([]);


    useEffect(() => {
        const load = async () => {
            try {
                const [data, userListingsResponse, childrenData, platformUrlsData] = await Promise.all([
                    getListingDetail(listingId),
                    getUserListingsByListingId(listingId).catch(err => {
                        console.error("無法取得 Owner 資料:", err);
                        return null;
                    }),
                    getListingChildrenById(listingId),
                    getListingPlatformUrls(listingId),
                ]);

                console.log("📦 Listing Data:", data);
                console.log("👥 UserListings Response:", userListingsResponse);

                const userListings = userListingsResponse?.Users || [];

                if (userListings.length === 0) {
                    console.warn("UserListings 是空陣列");
                }

                const ownerRecords = userListings.filter(ul => {
                    if (!ul.UserRoles || !Array.isArray(ul.UserRoles)) return false;
                    return ul.UserRoles.some(role => role.toLowerCase().includes("owner"));
                });

                const hpRecords = userListings.filter(ul => {
                    if (!ul.UserRoles || !Array.isArray(ul.UserRoles)) return false;
                    return ul.UserRoles.some(role =>
                        role.toLowerCase().includes("hostingpartner") ||
                        role.toLowerCase().includes("host partner") ||
                        role.toLowerCase() === "hp"
                    );
                });

                const formatUser = (record) => ({
                    name: `${record.UserFirstName || ''} ${record.UserLastName || ''}`.trim() || "N/A",
                    email: record.UserEmail || "N/A",
                    phone: record.UserPhone || "N/A",
                });

                setOwners(ownerRecords.map(formatUser));
                setHostPartners(hpRecords.map(formatUser));

                setListing({
                    thumbnail: data.Thumbnail,
                    listingId: data.Id,
                    kozyGuruName: data.KozyGuruName,
                    name: data.Name,
                    ownerBillingAddress: data.Street || "N/A",
                    city: data.City || "N/A",
                    address: data.Street || "N/A",
                    state: data.State || "",
                    zipCode: data.ZipCode || "",
                });

                const initialParentRow = {
                    kozyguruName: data.NickName,
                    platform: data.Platform,
                    url: data.PlatformUrl || "",
                    online: data.IsListed === true || data.IsListed === 1,
                    isActive: data.IsActive === true || data.IsActive === 1,
                    isPublishedToBookingEngine: data.IsPublishedToBookingEngine === true || data.IsPublishedToBookingEngine === 1,
                    __dropdownOpen: false,
                };
                setParentRow(initialParentRow);
                setOriginalParentRow(structuredClone(initialParentRow));

                const childrenArray = Array.isArray(childrenData) ? childrenData : [];
                const platformUrlMap = {};
                for (const item of (platformUrlsData || [])) {
                    platformUrlMap[item.Id] = item.PlatformUrl || "";
                }

                const mappedChildren = childrenArray
                    .map((child) => ({
                        id: child.Id,
                        kozyguruName: child.NickName,
                        platform: child.Platform,
                        url: platformUrlMap[child.Id] ?? child.PlatformUrl ?? "",
                        online: child.IsListed === true || child.IsListed === 1,
                        isActive: child.IsActive === true || child.IsActive === 1,
                        thumbnail: child.Thumbnail,
                        __dropdownOpen: false,
                    }))
                    .sort((a, b) => {
                        if (a.online !== b.online) return Number(b.online) - Number(a.online);
                        return (a.platform || "").localeCompare(b.platform || "");
                    });

                setChildrenRows(mappedChildren);
                setOriginalChildrenRows(structuredClone(mappedChildren));

            } catch (err) {
                console.error("❌ Error loading listing:", err);
            }
        };

        load();
    }, [listingId]);


    if (!listing) return <PageContainer>Loading...</PageContainer>;


    const handleSave = async () => {
        const id = listing.listingId;
        if (!id) {
            setErrorMessage("Listing ID not found. Unable to save.");
            setShowError(true);
            return;
        }

        // 存檔前的最後防線：Online 開著卻沒填 Platform URL 就擋掉，
        // 避免存進去之後前台顯示一個點了會導回原頁的死 tag。
        const invalidNames = [];
        if (parentRow.isActive && !parentRow.url?.trim()) {
            invalidNames.push(parentRow.kozyguruName || "Parent listing");
        }
        childrenRows.forEach((row) => {
            if (row.isActive && !row.url?.trim()) {
                invalidNames.push(row.kozyguruName || `Listing #${row.id}`);
            }
        });
        if (invalidNames.length > 0) {
            setErrorMessage(
                `Cannot save: the following listings are Online but missing a Platform URL:\n${invalidNames.join("\n")}`
            );
            setShowError(true);
            return;
        }

        const payload = [
            {
                listingId: id,
                platform: parentRow.platform,
                platformUrl: parentRow.url,
                isActive: parentRow.isActive,
                isPublishedToBookingEngine: parentRow.isPublishedToBookingEngine,
            },
            ...childrenRows.map((row) => ({
                listingId: row.id,
                platform: row.platform,
                platformUrl: row.url,
                isActive: row.isActive,
            }))
        ];
        try {
            await updateListingsBatch(payload);
            setOriginalParentRow(structuredClone(parentRow));
            setOriginalChildrenRows(structuredClone(childrenRows));
            setErrorMessage("Saved successfully!");
            setShowError(true);
        } catch (err) {
            console.error("❌ 儲存失敗:", err);
            setErrorMessage("Failed to save. Please try again.");
            setShowError(true);
        }
    };

    const handleDiscard = () => {
        setParentRow(structuredClone(originalParentRow));
        setChildrenRows(structuredClone(originalChildrenRows));
    };


    const isRowChanged = (current, original) => {
        if (!current || !original) return false;
        const { __dropdownOpen: _a, ...currentRest } = current;
        const { __dropdownOpen: _b, ...originalRest } = original;
        return JSON.stringify(currentRest) !== JSON.stringify(originalRest);
    };

    const parentChanged = isRowChanged(parentRow, originalParentRow) ? 1 : 0;

    const childrenChangedCount = childrenRows.filter((row, i) =>
        isRowChanged(row, originalChildrenRows[i])
    ).length;

    const changedCount = parentChanged + childrenChangedCount;
    const isDirty = changedCount > 0;

    const handleBack = () => {
        if (window.history.length > 1) navigate(-1);
        else navigate(`/dashboard/admin-listing${fromQuery ? `?${fromQuery}` : ""}`);
    };

    return (
        <PageContainer>
            {/* ========== Header ========== */}
            <HeaderBar>
                <Title>
                    {listing.kozyGuruName} ({listing.listingId})
                </Title>

                <HeaderActions>
                    <BackButton onClick={handleBack}>Back to List</BackButton>
                </HeaderActions>
            </HeaderBar>

            {/* ========== Tabs ========== */}
            {/* Statement / Invoice / Inspection / Contract 尚未實作（元件都還沒接上），
                鎖定不給點，避免點進去顯示跟其他 Tab 一樣的「coming soon」畫面誤以為是 bug。
                之後接上對應元件時，把該 Tab 名稱從 IMPLEMENTED_TABS 移除鎖定即可。 */}
            <Tabs>
                {["Overview", "Statement", "Invoice", "Inspection", "Contract"].map((t) => {
                    const isLocked = !IMPLEMENTED_TABS.includes(t);
                    return (
                        <TabButton
                            key={t}
                            active={activeTab === t}
                            disabled={isLocked}
                            title={isLocked ? "Coming soon" : undefined}
                            onClick={() => !isLocked && setActiveTab(t)}
                        >
                            {t}
                        </TabButton>
                    );
                })}
            </Tabs>

            <ScrollArea>
            {/* ========== Owner Cards ========== */}
            {owners.length > 0 ? owners.map((owner, i) => (
                <OwnerCard key={i}>
                    <AvatarBox>
                        {listing.thumbnail ? (
                            <img src={listing.thumbnail} alt="thumbnail" />
                        ) : "O"}
                    </AvatarBox>

                    <OwnerInfoWrapper>
                        <OwnerNameBlock>
                            Owner{owners.length > 1 ? ` ${i + 1}` : ""}: {owner.name}
                        </OwnerNameBlock>

                        <OwnerInfoGrid>
                            <OwnerRow>
                                <Label>Email :</Label>
                                <Value>{owner.email}</Value>
                            </OwnerRow>
                            <OwnerRow>
                                <Label>Phone :</Label>
                                <Value>{owner.phone}</Value>
                            </OwnerRow>
                            <OwnerRow>
                                <Label>Owner Address (Billing Address of Owner):</Label>
                                <Value>{listing.ownerBillingAddress}, {listing.city}</Value>
                            </OwnerRow>
                            <OwnerRow>
                                <Label>Listing Address (Address of Property):</Label>
                                <Value>{listing.address}, {listing.city}</Value>
                            </OwnerRow>
                        </OwnerInfoGrid>
                    </OwnerInfoWrapper>
                </OwnerCard>
            )) : (
                <OwnerCard>
                    <AvatarBox>
                        {listing.thumbnail ? (
                            <img src={listing.thumbnail} alt="thumbnail" />
                        ) : "O"}
                    </AvatarBox>
                    <OwnerInfoWrapper>
                        <OwnerNameBlock>Owner: N/A</OwnerNameBlock>
                    </OwnerInfoWrapper>
                </OwnerCard>
            )}

            {/* ========== Host Partner Cards ========== */}
            {hostPartners.map((hp, i) => (
                <OwnerCard key={i}>
                    <AvatarBox>HP</AvatarBox>

                    <OwnerInfoWrapper>
                        <OwnerNameBlock>
                            Host Partner{hostPartners.length > 1 ? ` ${i + 1}` : ""}: {hp.name}
                        </OwnerNameBlock>

                        <OwnerInfoGrid>
                            <OwnerRow>
                                <Label>Email :</Label>
                                <Value>{hp.email}</Value>
                            </OwnerRow>
                            <OwnerRow>
                                <Label>Phone :</Label>
                                <Value>{hp.phone}</Value>
                            </OwnerRow>
                        </OwnerInfoGrid>
                    </OwnerInfoWrapper>
                </OwnerCard>
            ))}

            {/* ========== Overview / Other Tabs ========== */}
            {activeTab === "Overview" ? (
                <OverviewTab
                    listing={listing}
                    parentRow={parentRow}
                    setParentRow={setParentRow}
                    childrenRows={childrenRows}
                    setChildrenRows={setChildrenRows}
                    onSave={handleSave}
                    onDiscard={handleDiscard}
                    isDirty={isDirty}
                    changedCount={changedCount}
                />
            ) : (
                <Card>{activeTab} content coming soon…</Card>
            )}

            {/* 
            ) : activeTab === "Invoice" ? (
                <InvoiceTab listingId={listingId} listing={listing} />
            ) : activeTab === "Inspection" ? (
                <InspectionTab listingId={listingId} listing={listing} />
            ) : activeTab === "Contract" ? (
                <ContractTab listingId={listingId} listing={listing} />
            */}
            </ScrollArea>

            {showError && (
                <ErrorPopup show={showError}>
                    <div style={{ whiteSpace: 'pre-line' }}>{errorMessage}</div>
                    <CloseButton onClick={() => setShowError(false)}>×</CloseButton>
                </ErrorPopup>
            )}

        </PageContainer>
    );
};

export default AdminPropertyPage;